import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { fmtDateTimeBoth } from '../utils/dates';
import TagPicker from '../components/TagPicker';
import { PRIORITY_AR } from '../utils/labels';

const FREQUENCY_AR = { daily: 'يومياً', weekly: 'أسبوعياً', monthly: 'شهرياً' };
const DAYS_AR = ['الأحد', 'الاثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت'];

function formatNext(d) {
  return fmtDateTimeBoth(d);
}

export default function RecurringTasks() {
  const { api, user } = useAuth();
  const toast = useToast();
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [workflows, setWorkflows] = useState([]);
  const [allTags, setAllTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);

  const emptyForm = {
    title_template: '',
    description: '',
    assigned_to: '',
    client_id: '',
    project_id: '',
    workflow_id: '',
    priority: 'medium',
    frequency: 'weekly',
    days_of_week: [0],
    day_of_month: 1,
    time_of_day: '09:00',
    counter_current: 0,
    estimated_minutes: '',
    tag_ids: [],
    is_active: true,
  };
  const [form, setForm] = useState(emptyForm);

  const load = () => {
    setLoading(true);
    api('/recurring-tasks').then(setItems).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    api('/users').then(setUsers).catch(() => {});
    api('/clients').then(setClients).catch(() => {});
    api('/projects').then(setProjects).catch(() => {});
    api('/workflows').then(setWorkflows).catch(() => {});
    api('/tags').then(setAllTags).catch(() => {});
  }, []);

  const openNew = () => {
    setEditing(null);
    setForm(emptyForm);
    setShowModal(true);
  };

  const openEdit = (r) => {
    setEditing(r);
    setForm({
      title_template: r.title_template,
      description: r.description || '',
      assigned_to: r.assigned_to || '',
      client_id: r.client_id || '',
      project_id: r.project_id || '',
      workflow_id: r.workflow_id || '',
      priority: r.priority,
      frequency: r.frequency,
      days_of_week: (r.days_of_week && r.days_of_week.length) ? r.days_of_week : [r.day_of_week ?? 0],
      day_of_month: r.day_of_month ?? 1,
      time_of_day: (r.time_of_day || '09:00').slice(0, 5),
      counter_current: r.counter_current ?? 0,
      estimated_minutes: r.estimated_minutes || '',
      tag_ids: Array.isArray(r.tag_ids) ? r.tag_ids : (r.tags ? r.tags.split(',').map(s => Number(s.trim())).filter(Boolean) : []),
      is_active: !!r.is_active,
    });
    setShowModal(true);
  };

  const save = async () => {
    try {
      const payload = { ...form };
      payload.tags = (form.tag_ids || []).join(',');
      delete payload.tag_ids;
      ['assigned_to', 'client_id', 'project_id', 'workflow_id', 'estimated_minutes', 'tags', 'description']
        .forEach(k => { if (!payload[k]) delete payload[k]; });

      if (editing) {
        await api(`/recurring-tasks/${editing.id}`, { method: 'PUT', body: JSON.stringify(payload) });
        toast.success('تم تحديث القالب');
      } else {
        await api('/recurring-tasks', { method: 'POST', body: JSON.stringify(payload) });
        toast.success('تم إنشاء القالب');
      }
      setShowModal(false);
      load();
    } catch (err) { toast.error(err.message); }
  };

  const toggleActive = async (r) => {
    try {
      await api(`/recurring-tasks/${r.id}`, {
        method: 'PUT',
        body: JSON.stringify({ ...r, is_active: !r.is_active })
      });
      toast.success(r.is_active ? 'تم الإيقاف' : 'تم التفعيل');
      load();
    } catch (err) { toast.error(err.message); }
  };

  const remove = async (r) => {
    if (!window.confirm(`حذف القالب "${r.title_template}"؟`)) return;
    try {
      await api(`/recurring-tasks/${r.id}`, { method: 'DELETE' });
      toast.success('تم الحذف');
      load();
    } catch (err) { toast.error(err.message); }
  };

  const spawnNow = async (r) => {
    try {
      const res = await api(`/recurring-tasks/${r.id}/spawn-now`, { method: 'POST' });
      toast.success(`تم إنشاء: ${res.title}`);
      load();
    } catch (err) { toast.error(err.message); }
  };

  const canEdit = ['admin', 'supervisor', 'team'].includes(user?.role);
  const canDelete = ['admin', 'supervisor'].includes(user?.role);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <h2 style={{ margin: 0 }}>المهام المتكررة</h2>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
            قوالب تُولّد مهاماً تلقائياً بجدول زمني وعدّاد تزايدي
          </div>
        </div>
        {canEdit && (
          <button className="btn btn-primary" data-tour="recurring-create" onClick={openNew}>+ قالب جديد</button>
        )}
      </div>

      {loading ? (
        <div className="skeleton-grid">{[...Array(3)].map((_, i) => <div key={i} className="skeleton-card" />)}</div>
      ) : items.length === 0 ? (
        <div className="card"><div className="empty"><div className="empty-icon">🔁</div>لا توجد قوالب متكررة بعد</div></div>
      ) : (
        <div className="card" style={{ padding: 0 }}>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>القالب</th>
                  <th>التكرار</th>
                  <th>العدّاد</th>
                  <th>التالي</th>
                  <th>المسؤول</th>
                  <th>الحالة</th>
                  <th>إجراءات</th>
                </tr>
              </thead>
              <tbody>
                {items.map(r => (
                  <tr key={r.id}>
                    <td>
                      <div style={{ fontWeight: 500 }}>{r.title_template}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-faint)' }}>
                        المثال: {r.title_template.includes('{n}')
                          ? r.title_template.replace('{n}', r.counter_current + 1)
                          : `${r.title_template} ${r.counter_current + 1}`}
                      </div>
                    </td>
                    <td style={{ fontSize: 13 }}>
                      {FREQUENCY_AR[r.frequency]}
                      {r.frequency === 'weekly' && (
                        <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                          {((r.days_of_week && r.days_of_week.length) ? r.days_of_week : [r.day_of_week ?? 0]).map(d => DAYS_AR[d]).join('، ')}
                        </div>
                      )}
                      {r.frequency === 'monthly' && <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>يوم {r.day_of_month}</div>}
                      <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{(r.time_of_day || '').slice(0, 5)}</div>
                    </td>
                    <td>
                      <span className="badge badge-in_progress">{r.counter_current}</span>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{r.tasks_count ?? 0} مهمة مُولَّدة</div>
                    </td>
                    <td style={{ fontSize: 12 }}>{formatNext(r.next_run_at)}</td>
                    <td>{r.assignee?.name || '—'}</td>
                    <td>
                      <span className={`badge ${r.is_active ? 'badge-completed' : 'badge-cancelled'}`}>
                        {r.is_active ? 'نشط' : 'موقّف'}
                      </span>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                        {canEdit && <button className="btn btn-sm btn-outline" onClick={() => spawnNow(r)} title="توليد مهمة الآن">▶</button>}
                        {canEdit && <button className="btn btn-sm btn-outline" onClick={() => toggleActive(r)} title={r.is_active ? 'إيقاف' : 'تفعيل'}>{r.is_active ? '⏸' : '▶'}</button>}
                        {canEdit && <button className="btn btn-sm btn-outline" onClick={() => openEdit(r)}>تعديل</button>}
                        {canDelete && <button className="btn btn-sm btn-outline" onClick={() => remove(r)} style={{ color: 'var(--danger)' }}>حذف</button>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-title">{editing ? 'تعديل قالب متكرر' : 'قالب متكرر جديد'}</div>

            <div className="form-group">
              <label className="form-label">عنوان القالب * <span style={{ fontWeight: 400, color: 'var(--text-muted)', fontSize: 11 }}>(استخدم <code>{'{n}'}</code> مكان الرقم، مثال: "مونتاج الدرس {'{n}'}")</span></label>
              <input className="form-input" value={form.title_template} onChange={e => setForm(f => ({ ...f, title_template: e.target.value }))} placeholder="مونتاج الدرس {n}" />
            </div>

            <div className="form-group">
              <label className="form-label">الوصف</label>
              <textarea className="form-textarea" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">التكرار *</label>
                <select className="form-select" value={form.frequency} onChange={e => setForm(f => ({ ...f, frequency: e.target.value }))}>
                  {Object.entries(FREQUENCY_AR).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">الوقت</label>
                <input className="form-input" type="time" value={form.time_of_day} onChange={e => setForm(f => ({ ...f, time_of_day: e.target.value }))} />
              </div>
            </div>

            {form.frequency === 'weekly' && (
              <div className="form-group">
                <label className="form-label">يوم الأسبوع</label>
                <div className="weekday-picker">
                  {DAYS_AR.map((d, i) => (
                    <button
                      key={i}
                      type="button"
                      className={`weekday${(form.days_of_week || []).includes(i) ? ' on' : ''}`}
                      onClick={() => setForm(f => {
                        const days = f.days_of_week || [];
                        return { ...f, days_of_week: days.includes(i) ? days.filter(x => x !== i) : [...days, i].sort((a, b) => a - b) };
                      })}
                    >{d}</button>
                  ))}
                </div>
              </div>
            )}

            {form.frequency === 'monthly' && (
              <div className="form-group">
                <label className="form-label">يوم الشهر (1-31)</label>
                <input className="form-input" type="number" min="1" max="31" value={form.day_of_month} onChange={e => setForm(f => ({ ...f, day_of_month: Number(e.target.value) }))} />
              </div>
            )}

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">العدّاد الحالي <span style={{ fontWeight: 400, color: 'var(--text-muted)', fontSize: 11 }}>(آخر رقم استُخدم، التالي = هذا + 1)</span></label>
                <input className="form-input" type="number" min="0" value={form.counter_current} onChange={e => setForm(f => ({ ...f, counter_current: Number(e.target.value) }))} />
              </div>
              <div className="form-group">
                <label className="form-label">الأولوية</label>
                <select className="form-select" value={form.priority} onChange={e => setForm(f => ({ ...f, priority: e.target.value }))}>
                  {Object.entries(PRIORITY_AR).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">المسار</label>
                <select className="form-select" value={form.workflow_id} onChange={e => {
                  const wid = e.target.value;
                  const wf = workflows.find(w => String(w.id) === String(wid));
                  const email = wf?.steps?.[0]?.default_assignee;
                  const u = email ? users.find(x => x.email === email) : null;
                  setForm(f => ({ ...f, workflow_id: wid, assigned_to: u ? String(u.id) : f.assigned_to }));
                }}>
                  <option value="">بدون مسار (يدوية)</option>
                  {workflows.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">المسؤول الافتراضي</label>
                <select className="form-select" value={form.assigned_to} onChange={e => setForm(f => ({ ...f, assigned_to: e.target.value }))}>
                  <option value="">غير محدد</option>
                  {users.filter(u => ['admin','supervisor','team'].includes(u.role)).map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">المشروع</label>
                <select className="form-select" value={form.project_id} onChange={e => setForm(f => ({ ...f, project_id: e.target.value }))}>
                  <option value="">بدون</option>
                  {projects.map(p => <option key={p.id} value={p.id}>{p.title}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">العميل / الشيخ</label>
                <select className="form-select" value={form.client_id} onChange={e => setForm(f => ({ ...f, client_id: e.target.value }))}>
                  <option value="">بدون</option>
                  {(() => {
                    if (!form.project_id) return clients;
                    const proj = projects.find(p => String(p.id) === String(form.project_id));
                    return proj?.sheikhs || [];
                  })().map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">الوقت المقدّر (دقائق)</label>
                <input className="form-input" type="number" min="0" value={form.estimated_minutes} onChange={e => setForm(f => ({ ...f, estimated_minutes: e.target.value }))} />
              </div>
              <div className="form-group">
                <label className="form-label">وسوم</label>
                <TagPicker value={form.tag_ids} onChange={ids => setForm(f => ({ ...f, tag_ids: ids }))} allTags={allTags} />
              </div>
            </div>

            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <input type="checkbox" checked={form.is_active} onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))} />
                <span>نشط (تلقائياً يولّد مهام في موعدها)</span>
              </label>
            </div>

            <div className="modal-actions">
              <button className="btn btn-primary" onClick={save} disabled={!form.title_template}>
                {editing ? 'حفظ' : 'إنشاء'}
              </button>
              <button className="btn btn-outline" onClick={() => setShowModal(false)}>إلغاء</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
